import {useMemo} from 'react';
import {FILTER_SELECT_TYPES, FILTER_TYPES} from '~/lib/const';
import navigation from '~/lib/navigation';
import {
  FilterOption,
  FilterOptionBoost,
  FilterOptionValue,
  ProductColorEntity,
} from '~/types';
import {sortByStringProperty} from '~/lib';

export const useFilterOption = (
  options: FilterOptionBoost[],
  colors: ProductColorEntity[],
): FilterOption[] => {
  return useMemo(() => {
    return options
      .filter((option) => option.status === 'active')
      .reduce((acc, option) => {
        if (option.filterType === FILTER_TYPES.COLLECTION) {
          // @ts-ignore
          const options = option.values.map((val) => ({
            label: val.label,
            value: val.id,
            href: navigation.collectionDetail.getPath(val.handle),
          }));

          if (options.length > 0) {
            acc.push({
              name: option.label,
              expanded: true,
              type: 'options',
              filterType: option.filterType,
              filterOptionId: option.filterOptionId,
              selectType: FILTER_SELECT_TYPES.LINK,
              options,
            });
          }
        } else if (option.filterType === FILTER_TYPES.TAG) {
          let optionsObj =
            option.values
              // @ts-ignore
              ?.map((v) => {
                return {
                  label: v.key.split(':')[1],
                  value: v.key,
                };
              })
              .sort(sortByStringProperty('label')) ?? [];

          // @ts-ignore
          if (Array.isArray(option.manualValues)) {
            // @ts-ignore
            const additionalValues =
              option.manualValues?.map((v) => {
                const value = typeof v === 'string' ? v : v.key;
                let label = typeof v === 'string' ? v : v.key;

                if (label.includes(':')) {
                  label = label.split(':')[1];
                }

                if (optionsObj.find((o: any) => o.label === label)) {
                  return null;
                }

                return {
                  label,
                  value,
                };
              }) ?? [];

            optionsObj = [
              ...optionsObj,
              ...additionalValues.filter((v) => Boolean(v)),
            ];
          }

          if (optionsObj.length > 0) {
            acc.push({
              name: option.label,
              expanded: true,
              type: 'options' as const,
              filterType: option.filterType,
              filterOptionId: option.filterOptionId,
              selectType: option.selectType,
              options: optionsObj,
            });
          }
        } else if (option.filterType === FILTER_TYPES.VENDOR) {
          // @ts-ignore
          const options = option.values.map((v) => ({
            label: v.key,
            value: v.key,
          }));

          if (options.length > 0) {
            acc.push({
              name: option.label,
              expanded: true,
              type: 'options' as const,
              filterType: option.filterType,
              filterOptionId: option.filterOptionId,
              selectType: option.selectType,

              options: option.values
                // @ts-ignore
                .map((v) => ({
                  label: v.key,
                  value: v.key,
                }))
                .sort(sortByStringProperty('label')),
            });
          }
        } else if (option.filterType === FILTER_TYPES.PRICE) {
          // @ts-ignore
          if (option.values.max === 0) return acc;

          acc.push({
            name: option.label,
            expanded: true,
            type: 'range' as const,
            filterType: option.filterType,
            filterOptionId: option.filterOptionId,
            selectType: FILTER_SELECT_TYPES.RANGE,
            min: 0,
            // @ts-ignore
            max: Math.ceil(option.values.max),
          });
        } else if (option.filterType.startsWith('opt')) {
          // Size options
          if (option.filterType === 'opt_size') {
            // @ts-ignore
            const options = option.values.map((v) => ({
              label: v.key,
              value: v.key,
            }));

            if (options.length > 0) {
              acc.push({
                name: option.label,
                expanded: true,
                type: 'options' as const,
                filterType: option.filterType,
                filterOptionId: option.filterOptionId,
                selectType: option.selectType,
                // @ts-ignore
                options: option.values.map((v) => ({
                  label: v.key,
                  value: v.key,
                })),
              });
            }
          } else if (option.filterType.endsWith('_size')) {
            const min = Math.min(
              ...option.values
                // @ts-ignore
                .map((v) => Math.floor(parseFloat(v.key)))
                .filter((v: number) => !isNaN(v)),
            );
            const max = Math.max(
              ...option.values
                // @ts-ignore
                .map((v) => Math.ceil(parseFloat(v.key)))
                .filter((v: number) => !isNaN(v)),
            );

            const options = option.values
              // @ts-ignore
              .filter((v) => v.doc_count > 0)
              // @ts-ignore
              .map((v) => ({
                label: v.key,
                value: v.key,
                color: v.key.toLowerCase(),
              }));

            if (options.length > 0) {
              acc.push({
                name: option.label,
                expanded: true,
                type: 'range' as const,
                filterType: option.filterType,
                filterOptionId: option.filterOptionId,
                selectType: FILTER_SELECT_TYPES.RANGE,
                min,
                max,
                hideCurrencySymbol: true,
                options,
              });
            }
          } else if (option.filterType.includes('color')) {
            const options: FilterOptionValue[] = [];

            // @ts-ignore
            for (const value of option.values) {
              const color = colors.find((c) =>
                c.shopifyColors?.includes(value.key),
              );

              if (color) {
                const optionAlreadyAdded = options.find(
                  (o) => o.color === color.color?.hex,
                );

                if (optionAlreadyAdded) {
                  optionAlreadyAdded.values?.push(value.key);
                } else {
                  options.push({
                    label: value.key,
                    value: color.title || value.key,
                    values: [value.key],
                    color: color.color?.hex,
                  });
                }
              } else {
                options.push({
                  label: value.key,
                  value: value.key,
                  color: value.key.toLowerCase(),
                });
              }
            }

            if (options.length > 0) {
              // Options like color
              acc.push({
                name: option.label,
                expanded: true,
                type: 'color',
                filterType: option.filterType,
                filterOptionId: option.filterOptionId,
                selectType: option.selectType,
                options,
              });
            }
          } else {
            // @ts-ignore
            const options = option.values.map((v) => ({
              label: v.key,
              value: v.key,
              color: v.key.toLowerCase(),
            }));

            if (options.length > 0) {
              // Other options
              acc.push({
                name: option.label,
                expanded: true,
                type: 'options' as const,
                filterType: option.filterType,
                filterOptionId: option.filterOptionId,
                selectType: option.selectType,
                options,
              });
            }
          }
        } else if (option.filterType === FILTER_TYPES.STOCK) {
          // @ts-ignore
          const options = option.values.map((v) => ({
            label: v.label,
            value: String(v.key === 'in-stock'),
          }));

          if (options.length > 0) {
            acc.push({
              name: option.label,
              expanded: true,
              type: 'options' as const,
              filterType: option.filterType,
              filterOptionId: option.filterOptionId,
              selectType: FILTER_SELECT_TYPES.SINGLE,
              // @ts-ignore
              options: option.values.map((v) => ({
                label: v.label,
                value: String(v.key === 'in-stock'),
              })),
            });
          }
        } else {
          console.error(
            `ProductFilter: Unhandled filter type ${option.filterType}`,
          );
        }
        return acc;
      }, [] as FilterOption[]);
  }, [options, colors]);
};
